import { appInsights } from "../../modules/appInsights/appInsights";

// =============================
// Interfaccia per il contesto dei log
// =============================
interface LogContext {
  [key: string]: string | number | boolean | undefined | null;
}

// =============================
// Configurazione
// =============================
type LogLevel = "debug" | "info" | "warn" | "error";
const isProduction = process.env.NODE_ENV === "production";
const logLevel: LogLevel = isProduction ? "info" : "debug";

// =============================
// Interfaccia pubblica
// =============================
const logger = {
  /**
   * Logga informazioni di debug (solo in dev)
   */
  debug: (message: string, context?: LogContext): void => {
    if (logLevel === "debug") {
      console.debug(`[DEBUG] ${message}`, context || "");
    }
  },

  /**
   * Logga informazioni utili per il monitoraggio
   */
  info: (message: string, context?: LogContext): void => {
    console.info(`[INFO] ${message}`, context || "");
    trackEvent("AppInfo", { message, ...context });
  },

  /**
   * Logga avvisi non critici
   */
  warn: (message: string, context?: LogContext): void => {
    console.warn(`[WARN] ${message}`, context || "");
    trackEvent("AppWarning", { message, ...context });
  },

  /**
   * Logga errori gravi con contesto
   */
  error: (error: Error | string, context?: LogContext): void => {
    const errorMessage = typeof error === "string" ? error : error.message;
    const errorStack = typeof error === "string" ? undefined : error.stack;

    console.error(`[ERROR] ${errorMessage}`, {
      stack: errorStack,
      ...context,
    });

    trackException(errorMessage, {
      stack: errorStack,
      ...context,
    });
  },
};

// =============================
// Funzioni interne - Application Insights
// =============================
function trackEvent(name: string, properties?: Record<string, unknown>): void {
  if (appInsights && appInsights.trackEvent) {
    appInsights.trackEvent({ name }, properties);
  }
}

function trackException(message: string, context?: Record<string, unknown>): void {
  if (appInsights && appInsights.trackException) {
    const exception = new Error(message);
    appInsights.trackException({
      exception,
      properties: context,
    });
  }
}

export default logger;